import { getUserMessageDisplayText } from '@terra/ui-design-system';
import type { ChatTabType } from '../../store/chatTabsStore';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { getQueuedMessageText } from './QueuedMessagesPanel';

const DRAFT_PREVIEW_MAX_LENGTH = 40;

export function normalizeConversationTitle(
  title: string | null | undefined,
): string {
  return title?.trim() ?? '';
}

export function truncateDraftPreview(text: string): string {
  const singleLine = text.replace(/\s+/g, ' ').trim();

  if (!singleLine) {
    return '';
  }

  if (singleLine.length <= DRAFT_PREVIEW_MAX_LENGTH) {
    return singleLine;
  }

  return `${singleLine.slice(0, DRAFT_PREVIEW_MAX_LENGTH)}…`;
}

export function resolveChatTabPreviewText(tab: ChatTabType): string | null {
  if (tab.conversationId) {
    return null;
  }

  const draftInput = tab.draftInput?.trim();

  if (draftInput) {
    return truncateDraftPreview(draftInput);
  }

  const chat = useChatInstancesStore.getState().chats.get(tab.chatId);
  const firstUserMessage = chat?.messages.find(
    (message) => message.role === 'user',
  );

  if (firstUserMessage) {
    const messageText = getUserMessageDisplayText({
      message: firstUserMessage,
    }).trim();

    if (messageText) {
      return truncateDraftPreview(messageText);
    }
  }

  const queuedMessages = useChatInstancesStore
    .getState()
    .getQueuedMessages({ chatId: tab.chatId });
  const firstQueuedMessage = queuedMessages[0];

  if (firstQueuedMessage) {
    const queuedText = getQueuedMessageText({
      queuedMessage: firstQueuedMessage,
    }).trim();

    if (queuedText) {
      return truncateDraftPreview(queuedText);
    }
  }

  return null;
}

export function hasDraftChatTabContent(tab: ChatTabType): boolean {
  return !tab.conversationId && resolveChatTabPreviewText(tab) !== null;
}

export function resolveCustomConversationTitle({
  tab,
}: {
  tab: ChatTabType | undefined;
}): string | undefined {
  if (!tab?.hasCustomTitle) {
    return undefined;
  }

  const title = normalizeConversationTitle(tab.title);

  return title || undefined;
}

export function resolveChatTabDisplayTitle({
  tab,
  fallbackTitle,
  draftTitle,
}: {
  tab: ChatTabType;
  fallbackTitle: string;
  draftTitle: (preview: string) => string;
}): string {
  const customTitle = resolveCustomConversationTitle({ tab });

  if (customTitle) {
    return customTitle;
  }

  const preview = resolveChatTabPreviewText(tab);

  if (!preview) {
    return normalizeConversationTitle(tab.title) || fallbackTitle;
  }

  return draftTitle(preview);
}
